import { useEffect } from 'react';
import { LateralityValue } from 'utils/lib/fhir/radiology';
import {
  CPTCodeDTO,
  DiagnosisDTO,
  VitalsBloodPressureObservationDTO,
  VitalsHeartbeatObservationDTO,
  VitalsHeightObservationDTO,
  VitalsLastMenstrualPeriodObservationDTO,
  VitalsOxygenSatObservationDTO,
  VitalsRespirationRateObservationDTO,
  VitalsTemperatureObservationDTO,
  VitalsVisionObservationDTO,
  VitalsWeightObservationDTO,
} from 'utils/lib/types/api/chart-data/chart-data.types';
import { MedicationData } from 'utils/lib/types/api/medication-administration.types';
import { ProcedurePageState } from 'utils/lib/types/api/procedures.types';
import { CreateRadiologyZambdaOrderInput } from 'utils/lib/types/api/radiology';
import {
  InputImmunizationOrderAdministrationDetails,
  InputImmunizationOrderDetails,
} from 'utils/lib/types/data/immunization/types';
import { CreateInHouseLabOrderParameters } from 'utils/lib/types/data/in-house/in-house.types';
import { CreateLabOrderParameters } from 'utils/lib/types/data/labs/labs.types';
import { CreateNursingOrderInput } from 'utils/lib/types/data/orders/types';
import { create, Mutate, StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// add key for any section that should have persisted "draft" data
type StoreKey =
  | 'external-lab'
  | 'in-house-lab'
  | 'radiology'
  | 'procedure'
  | 'nursing-order'
  | 'immunization'
  | 'in-house-med'
  | 'vitals';

const DRAFT_STORE_NAME_BY_KEY: Record<StoreKey, string> = {
  'external-lab': 'create-external-lab-order-draft',
  'in-house-lab': 'create-in-house-lab-order-draft',
  radiology: 'create-radiology-order-draft',
  procedure: 'create-procedure-draft',
  'nursing-order': 'nursing-order-draft',
  immunization: 'immunization-order-draft',
  'in-house-med': 'in-house-med-order-draft',
  vitals: 'vitals-draft',
};

type GenericStateDraft = {
  hasNavigatedAway: boolean;
};

type CreateExternalLabOrderDraft = Partial<
  Omit<CreateLabOrderParameters, 'encounter' | 'orderingLocation'> & {
    orderingLocationId: string;
  } & GenericStateDraft
>;
type CreateInHouseLabOrderDraft = Partial<
  Omit<CreateInHouseLabOrderParameters, 'diagnosesAll' | 'diagnosesNew'> & {
    selectedNewDx: DiagnosisDTO[];
    selectedAssessmentDx: DiagnosisDTO[];
  } & GenericStateDraft
>;
type CreateRadiologyOrderDraft = Partial<
  Omit<CreateRadiologyZambdaOrderInput, 'diagnosisCodes' | 'cptCode' | 'lateralityModifier'> & {
    dx: DiagnosisDTO[];
    cptCode: CPTCodeDTO;
    laterality: LateralityValue | '';
  } & GenericStateDraft
>;
type CreateProcedureDraft = Partial<ProcedurePageState & GenericStateDraft>;
type CreateNursingOrderDraft = Partial<CreateNursingOrderInput & GenericStateDraft>;
type CreateImmunizationOrderDraft = Partial<
  InputImmunizationOrderDetails &
    GenericStateDraft & { administrationDetails: InputImmunizationOrderAdministrationDetails }
>;
type CreateInHouseMedicationOrderDraft = Partial<MedicationData & GenericStateDraft>;
type VitalsDraft = Partial<
  {
    // Partial<> on these four allows omitting numeric fields when the value is NaN
    // (qualifier-only edits), avoiding NaN→null corruption in JSON sessionStorage.
    temperature: Partial<VitalsTemperatureObservationDTO>;
    heartbeat: Partial<VitalsHeartbeatObservationDTO>;
    bloodPressure: Partial<VitalsBloodPressureObservationDTO>;
    oxygenSat: Partial<VitalsOxygenSatObservationDTO>;
    respirationRate: VitalsRespirationRateObservationDTO;
    weight: VitalsWeightObservationDTO;
    height: VitalsHeightObservationDTO;
    vision: VitalsVisionObservationDTO;
    lmp: VitalsLastMenstrualPeriodObservationDTO;
  } & GenericStateDraft
>;

interface DraftState<TDraft extends object> {
  draftsByEncounterId: Record<string, TDraft>;
  setDraft: (encounterId: string, draftData: TDraft) => void;
  clearDraft: (encounterId: string) => void;
  hasDraft: (encounterId: string) => boolean;
  getDraft: (encounterId: string) => TDraft;
}

function createGenericStore<TDraft extends object>(
  storeKey: StoreKey
): UseBoundStore<Mutate<StoreApi<DraftState<TDraft>>, [['zustand/persist', unknown]]>> {
  return create<DraftState<TDraft>>()(
    persist(
      (set, get) => ({
        draftsByEncounterId: {},
        setDraft: (encounterId, draftData) =>
          set((state) => ({
            draftsByEncounterId: {
              ...state.draftsByEncounterId,
              [encounterId]: { ...(get().draftsByEncounterId[encounterId] ?? {}), ...draftData },
            },
          })),
        clearDraft: (encounterId) =>
          set((state) => {
            const updatedState = { ...state.draftsByEncounterId };
            delete updatedState[encounterId];
            return { draftsByEncounterId: updatedState };
          }),
        hasDraft: (encounterId) => {
          const draft = get().draftsByEncounterId[encounterId];
          if (!draft) return false;
          return Object.entries(draft as Record<string, unknown>).some(
            ([key, value]) => key !== 'hasNavigatedAway' && value !== undefined
          );
        },
        getDraft: (encounterId) => get().draftsByEncounterId[encounterId] ?? {},
      }),
      { name: DRAFT_STORE_NAME_BY_KEY[storeKey], storage: createJSONStorage(() => sessionStorage) }
    )
  );
}

export const useCreateExternalLabStore = createGenericStore<CreateExternalLabOrderDraft>('external-lab');
export const useCreateInHouseLabStore = createGenericStore<CreateInHouseLabOrderDraft>('in-house-lab');
export const useCreateRadiologyOrderStore = createGenericStore<CreateRadiologyOrderDraft>('radiology');
export const useProcedureStore = createGenericStore<CreateProcedureDraft>('procedure');
export const useNursingOrderStore = createGenericStore<CreateNursingOrderDraft>('nursing-order');
export const useImmunizationOrderStore = createGenericStore<CreateImmunizationOrderDraft>('immunization');
export const useInHouseMedicationOrderStore = createGenericStore<CreateInHouseMedicationOrderDraft>('in-house-med');
export const useVitalsDraftStore = createGenericStore<VitalsDraft>('vitals');

/**
 * Adds listeners to track if a user navigates away from the page and marks the draft as navigated away.
 * Useful for conditional warning rendering
 * @param input
 */
export function useMarkDraftNavigatedAway(input: {
  encounterId: string;
  setDraft: (encounterId: string, draftData: Partial<GenericStateDraft>) => void;
  hasDraft: (encounterId: string) => boolean;
}): void {
  const { encounterId, setDraft, hasDraft } = input;

  useEffect(() => {
    const markNavigatedAway = (): void => {
      if (encounterId && hasDraft(encounterId)) {
        setDraft(encounterId, {
          hasNavigatedAway: true,
        });
      }
    };

    window.addEventListener('beforeunload', markNavigatedAway);

    return () => {
      window.removeEventListener('beforeunload', markNavigatedAway);
      markNavigatedAway();
    };
  }, [encounterId, hasDraft, setDraft]);
}
